import { makeAutoObservable, reaction } from "mobx";

import { BN } from "@utils/BN";
import {
  convertFillResponseListToFillList,
  convertOrderResponseListToOrderList,
  convertPositionResponseListToPositionList,
} from "@utils/apiConverters";

import type { FillList, OrderList, PositionList } from "@src/api";
import { orderService } from "@src/services";

import type { RootStore } from "./RootStore";

export type OpenOrdersRow = {
  id: number;
  date: Date;
  market: string;
  marketAddr: string;
  symbol: string;
  subAccountAddress: string;
  type: string;
  price: string;
  priceRaw: BN;
  sizeRaw: BN;
  size: string;
  filled: string;
  filledPercent: string;
  trigger: string;
  cancel: string;
  leverage: string;
  sid: string;
  side: "buy" | "sell";
  quoteDecimals: number;
  baseDecimals: number;
  stopLoss?: string;
  takeProfit?: string;
};

export class MyOrdersStore {
  openBalancesByToken = new Map<string, BN>();
  openOrders: OrderList = [];
  tradeHistory: FillList = [];
  orderHistory: OrderList = [];
  fillHistory: FillList = [];
  openPosition: PositionList = [];
  isLoadingOrders = false;

  constructor(private readonly rootStore: RootStore) {
    makeAutoObservable(this);
    this.setupReactions();
  }

  setOpenBalanceByToken = (market: string, size: BN) => {
    this.openBalancesByToken.set(market, size);
  };

  setOpenOrders = (value: OrderList) => {
    this.openOrders = value;
  };

  setTradeHistory = (value: FillList) => {
    this.tradeHistory = value;
  };

  setOrderHistory = (value: OrderList) => {
    this.orderHistory = value;
  };

  setFillHistory = (value: FillList) => {
    this.fillHistory = value;
  };

  setOpenPosition = (value: PositionList) => {
    this.openPosition = value;
  };

  setLoadingOrders = (loading: boolean) => {
    this.isLoadingOrders = loading;
  };

  updateOrders = (newOrders: OrderList) => {
    const ordersMap = new Map(this.openOrders.map((order) => [order.sid, order]));

    const isOpenStatus = (status: string) =>
      ["placed", "partial_filled", "new", "partiallyfilled", "cancelrequested"].includes(status.toLowerCase());

    const isMarketOrder = (type: string) => type.toLowerCase() === "market";

    const closedOrders: OrderList = [];

    newOrders.forEach((newOrder) => {
      if (isMarketOrder(newOrder.type)) {
        ordersMap.delete(newOrder.sid);
        closedOrders.push(newOrder);
      } else if (isOpenStatus(newOrder.status)) {
        ordersMap.set(newOrder.sid, newOrder);
      } else {
        ordersMap.delete(newOrder.sid);
        closedOrders.push(newOrder);
      }
    });

    this.openOrders = Array.from(ordersMap.values());

    // Add closed orders to order history
    if (closedOrders.length > 0) {
      const existingOrderIds = new Set(this.orderHistory.map((o) => o.sid));
      const uniqueClosedOrders = closedOrders.filter((o) => !existingOrderIds.has(o.sid));
      if (uniqueClosedOrders.length > 0) {
        this.orderHistory = [...uniqueClosedOrders, ...this.orderHistory];
      }
    }
  };

  addNewFills = (newFills: FillList) => {
    const existingFillIds = new Set(this.fillHistory.map((f) => f.sid));
    const uniqueNewFills = newFills.filter((f) => !existingFillIds.has(f.sid));
    this.fillHistory = [...uniqueNewFills, ...this.fillHistory];

    const existingTradeIds = new Set(this.tradeHistory.map((f) => f.sid));
    const uniqueNewTrades = newFills.filter((f) => !existingTradeIds.has(f.sid));
    this.tradeHistory = [...uniqueNewTrades, ...this.tradeHistory];
  };

  updatePositions = (newPositions: PositionList) => {
    const positionsMap = new Map(this.openPosition.map((pos) => [pos.sid, pos]));

    newPositions.forEach((newPos) => {
      if (newPos.size !== 0) {
        positionsMap.set(newPos.sid, newPos);
      } else {
        positionsMap.delete(newPos.sid);
      }
    });

    this.openPosition = Array.from(positionsMap.values());
  };

  loadUserOrdersForMarket = async (_marketAddr: string): Promise<boolean> => {
    const rootToken = this.rootStore.accountStore.rootAccountToken;
    const subAccountAddress = this.rootStore.accountStore.activeTradingAccount?.address;
    if (!rootToken || !subAccountAddress) {
      return false;
    }

    this.setLoadingOrders(true);
    try {
      const result = await orderService.loadUserOrders(subAccountAddress);

      const marketsMap = new Map(this.rootStore.marketStore.allMarkets.map((m) => [m.addr, m]));
      const activeTradingAccountAddr = this.rootStore.accountStore.activeTradingAccount?.address;

      const orders = convertOrderResponseListToOrderList(result.orders, marketsMap);
      const nonMarketOrders = orders.filter((o) => o.type.toLowerCase() !== "market");
      this.setOpenOrders(nonMarketOrders);

      const orderHistory = convertOrderResponseListToOrderList(result.orderHistory, marketsMap);
      this.setOrderHistory(orderHistory);

      const fills = convertFillResponseListToFillList(result.fills, activeTradingAccountAddr, marketsMap);
      this.setTradeHistory(fills);

      const fillHistory = convertFillResponseListToFillList(result.fillHistory, activeTradingAccountAddr, marketsMap);
      this.setFillHistory(fillHistory);

      const positions = convertPositionResponseListToPositionList(result.positions, marketsMap);
      this.setOpenPosition(positions);

      this.extractLeveragesFromPositions(result.allPositions, activeTradingAccountAddr);
      return true;
    } catch (error) {
      console.error("[MyOrdersStore] Failed to load user orders:", error);
      return false;
    } finally {
      this.setLoadingOrders(false);
    }
  };

  private extractLeveragesFromPositions = (
    positions: Array<{ symbol: string; leverage: string }>,
    subAccountAddr?: string,
  ) => {
    const { marketStore } = this.rootStore;
    const leverages: Array<{ market_addr: string; leverage: number; sub_account_address: string }> = [];

    for (const position of positions) {
      const market = marketStore.allMarkets.find((m) => m.symbol === position.symbol);
      if (!market) continue;

      const leverage = Number(position.leverage);
      if (leverage > 0) {
        leverages.push({
          market_addr: market.addr,
          leverage,
          sub_account_address: subAccountAddr || "",
        });
      }
    }

    if (leverages.length > 0) {
      marketStore.setLeverages(leverages);
    }
  };

  private setupReactions = () => {
    const { accountStore, marketStore, configStore } = this.rootStore;

    reaction(
      () => [this.openOrders, marketStore.allMarkets],
      () => {
        this.prepareOpenBalances(this.openOrders);
      },
    );

    let lastLoadedKey: string | null = null;
    reaction(
      () =>
        [
          accountStore.rootAccountToken,
          accountStore.isFullyConnected,
          accountStore.activeTradingAccount?.address,
          marketStore.activeMarket?.addr,
          // Use isSdkReady (not isInitialized) so this never fires before
          // setConfigReady(true) + resetEkidenSdkClient() have run in
          // ConfigProvider — otherwise REST calls land on a client with no token.
          configStore.isSdkReady,
        ] as const,
      ([rootToken, isFullyConnected, subAccountAddress, marketAddr, isSdkReady]) => {
        if (!rootToken || !isFullyConnected || !isSdkReady) {
          lastLoadedKey = null;
          return;
        }

        const loadKey = `${subAccountAddress}-${marketAddr}`;
        if (subAccountAddress && marketAddr && lastLoadedKey !== loadKey) {
          lastLoadedKey = loadKey;
          this.loadUserOrdersForMarket(marketAddr).then((success) => {
            if (!success) {
              lastLoadedKey = null;
            }
          });
        }
      },
      { fireImmediately: true },
    );
  };

  private prepareOpenBalances = (orders: OrderList) => {
    const { marketStore } = this.rootStore;

    this.openBalancesByToken.clear();

    orders.forEach((order) => {
      const market = marketStore.allMarkets.find((m) => m.addr === order.market_addr);

      if (!market) return;

      const size = BN.formatUnits(order.size, market.base_decimals);
      const price = BN.formatUnits(order.price, market.quote_decimals);

      let total = this.openBalancesByToken.get(market.quote_addr) ?? BN.ZERO;
      total = total.plus(size.multipliedBy(price).dividedBy(order.leverage));
      this.setOpenBalanceByToken(market.quote_addr, total);
    });
  };

  reset = () => {
    this.openBalancesByToken.clear();
    this.openOrders = [];
    this.tradeHistory = [];
    this.orderHistory = [];
    this.openPosition = [];
    this.fillHistory = [];
  };
}
